"use client";
import { useState, useLayoutEffect, memo, useEffect } from "react";
import { useSelectedLayoutSegment } from "next/navigation";
import { Sling as Hamburger } from "hamburger-react";
import useFramerMotion, { LazyMotion, m } from "@/hooks/useFramerMotion";
import GetInTouchButton from "@/components/ui/GetInTouchButton";
import ContactInfo from "@/components/ui/ContactInfo";
import Social from "@/components/ui/Social";
import { useEventListener } from "usehooks-ts";
import { useLockBodyScroll } from "@/hooks/useLockBodyScroll";
import { ShootingStars } from "@/components/ui/ShootingStars";
import { Link } from "@/components/Link";
import { useTranslations } from "next-intl";
import { routes } from "./Nav";
import LanguageSwitcher from "./LanguageSwitcher";
import { useBreakpoint } from "@/hooks/useBreakpoint";

const PureShootingStars = memo(ShootingStars);

const mobileMenuVariant = {
  opened: {
    height: "auto",
    minHeight: "100dvh",
    transition: {
      delay: 0.15,
      duration: 1.1,
      ease: [0.74, 0, 0.19, 1.02],
    },
  },
  closed: {
    height: "0",
    minHeight: "0",
    transition: {
      delay: 0.35,
      duration: 0.63,
      ease: [0.74, 0, 0.19, 1.02],
    },
  },
};

const ulVariant = {
  opened: {
    transition: {
      delayChildren: 1,
      staggerChildren: 0.06,
    },
  },
  closed: {
    transition: {
      staggerChildren: 0.06,
      staggerDirection: -1,
    },
  },
};

const liVariant = {
  opened: {
    opacity: 1,
    y: "0%",
    transition: {
      duration: 0.25,
      ease: "easeOut",
    },
  },
  closed: {
    opacity: 0,
    y: "100%",
    transition: {
      duration: 0.25,
      ease: "easeInOut",
    },
  },
};

const fadeInVariant = {
  opened: {
    opacity: 1,
    transition: {
      delay: 1.2,
    },
  },
  closed: { opacity: 0 },
};

const NavMobile = () => {
  const { xl } = useBreakpoint();
  const selectedLayoutSegment = useSelectedLayoutSegment();
  const pathname = selectedLayoutSegment ? `/${selectedLayoutSegment}` : "/";

  const framerMotionFeatures = useFramerMotion();
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const { lock, unlock } = useLockBodyScroll();
  const t = useTranslations("nav");

  const handleKeyDown = (e: KeyboardEvent) => {
    if (e.key === "Escape" && mobileNavOpen) setMobileNavOpen(false);
  };

  useEventListener("keydown", handleKeyDown);

  useEffect(() => {
    if (mobileNavOpen) setMobileNavOpen(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname]);

  useLayoutEffect(() => {
    if (mobileNavOpen) {
      lock();
    } else {
      unlock();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mobileNavOpen]);

  useLayoutEffect(() => {
    if (xl) setMobileNavOpen(false);
  }, [xl]);

  return (
    <LazyMotion features={framerMotionFeatures}>
      <m.div
        animate={mobileNavOpen ? "opened" : "closed"}
        className="xl:hidden">
        <div className={"relative z-30"}>
          <Hamburger
            toggled={mobileNavOpen}
            toggle={setMobileNavOpen}
            size={40}
            direction="right"
            label="Show menu"
            hideOutline={false}
          />
        </div>
        <m.div
          variants={mobileMenuVariant}
          className="absolute left-0 top-0 flex h-0 min-h-0 w-screen flex-col items-center justify-center overflow-hidden bg-dark">
          <m.nav
            variants={ulVariant}
            className="flex flex-1 flex-col items-center justify-evenly pb-36 pt-20 text-center">
            {routes.map((item) => {
              const isActive = pathname === item.path;
              return (
                <m.div
                  key={item.key}
                  whileTap={{ scale: 0.95 }}
                  className="overflow-hidden">
                  <m.div variants={liVariant}>
                    <Link
                      href={item.path}
                      onClick={() => setMobileNavOpen(false)}
                      className={`${
                        isActive ? "text-accent" : ""
                      } relative select-none text-xl font-medium tracking-wide transition-all hover:text-accent`}>
                      {t(item.key)}
                      {isActive && (
                        <m.div
                          layoutId="activeItem-mobile"
                          transition={{
                            type: "spring",
                            bounce: 0.3,
                            duration: 0.6,
                          }}
                          className="absolute bottom-[-2px] left-0 h-[2px] w-full bg-accent"
                        />
                      )}
                    </Link>
                  </m.div>
                </m.div>
              );
            })}
            <m.div
              key={"hire-me"}
              whileTap={{ scale: 0.95 }}>
              <m.div variants={liVariant}>
                <GetInTouchButton />
              </m.div>
            </m.div>
            <m.div
              key={"language-switcher"}
              whileTap={{ scale: 0.95 }}>
              <m.div
                variants={liVariant}
                className="flex justify-center">
                <LanguageSwitcher />
              </m.div>
            </m.div>
          </m.nav>
          <m.div
            variants={fadeInVariant}
            className="absolute bottom-0 z-10 flex flex-col gap-8 pb-6 text-sm">
            <ContactInfo />
            <Social
              containerStyles="flex justify-center gap-4"
              iconStyles="size-10 border border-white rounded-full flex justify-center items-center text-white text-xl bg-white/0 hover:bg-white hover:text-dark hover:transition-all duration-500"
            />
          </m.div>
          {mobileNavOpen && <PureShootingStars />}
        </m.div>
      </m.div>
    </LazyMotion>
  );
};

export default NavMobile;
